// Ansicht Signalgraph.
//
// Linke Palette: Blocktypen aus GET /palette/blocks (nach category gruppiert) plus die
// Sonder-Knoten catalog-input/catalog-output (referenzieren eine Katalog-Variable per params.tag,
// per Dropdown aus dem geladenen Katalog) und der Platzhalter model-inference. Knoten platzieren,
// Ports (1-indiziert) über React-Flow-Kanten verdrahten. Rechtes Panel editiert die params je
// Knoten. „Graph speichern" → PUT /projects/{id}/graph; „Validieren" → POST .../graph/validate
// mit klarer Fehleranzeige (unresolved_tag etc.). Das erzeugte graph.json ist schema-konform
// (graph.schema.json) — die Übersetzung liegt in graph/build.ts.

import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import {
  addEdge,
  Background,
  Controls,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Connection,
} from '@xyflow/react';
import { useI18n } from '../i18n';
import { useStore } from '../state/store';
import {
  deleteSuperblock,
  getBlocks,
  getCatalog,
  getSuperblocks,
  getSuperblockGraph,
  importSuperblocks,
  listModels,
  listProjects,
  putGraph,
  validateGraph,
} from '../api/client';
import { SuperblockBulkImportDialog } from './SuperblockBulkImportDialog';
import { ModbusMappingDialog } from './ModbusMappingDialog';
import type {
  BlockType,
  BorisImportResult,
  CatalogVariable,
  ModelSummary,
  SignalGraph,
  SuperblockSummary,
  ValidateFinding,
} from '../api/types';
import { anzeigeMitBeleg } from '../graph/anzeige';
import { GroupNode, SignalNode } from '../components/nodes';
import { BorisImportDialog } from './BorisImportDialog';
import {
  buildGraph,
  graphToFlow,
  specialNodePorts,
  type FlowGraphEdge,
  type FlowGraphNode,
  type GraphNodeData,
} from '../graph/build';
import { computeLayout } from '../graph/layout';
import { groupByKind, kindOf } from '../graph/signalKind';
import { deriveGroups, groupBoxes } from '../graph/group';
import { Button, EmptyState, LabeledInput, LabeledSelect, PanelHeading } from '../components/ui';
import { CanvasDrawerBar, SidePanel, useNarrowViewport } from '../components/responsive';

const nodeTypes = { signal: SignalNode, groupBox: GroupNode };

// Sonder-Knoten der Palette, die nicht aus boris2py stammen.
const SPECIAL_TYPES = [
  'catalog-input',
  'signal-filter',
  'catalog-output',
  'model-inference',
] as const;

export function SignalView(): JSX.Element {
  const { t } = useI18n();
  const {
    activeProject,
    activeProjectId,
    catalog,
    graph,
    setGraph,
    setCatalog,
    setProjects,
    setActiveProjectId,
    notify,
    reportError,
  } = useStore();

  const [blocks, setBlocks] = useState<BlockType[]>([]);
  const [superblocks, setSuperblocks] = useState<SuperblockSummary[]>([]);
  const [paletteSearch, setPaletteSearch] = useState<string>('');
  // Navigations-Stack der aufgeklappten Superblock-Ebenen (statt eines einzelnen expandRef).
  // Jede Ebene trägt Referenz + Anzeigetitel; die oberste Ebene ist die aktuell gezeigte. Ein
  // Doppelklick auf einen verschachtelten Superblock schiebt eine Ebene auf, „Zurück" poppt eine.
  const [drilldown, setDrilldown] = useState<SuperblockLevel[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowGraphNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowGraphEdge>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Offen/zu der beiden Seitenpanele auf dem Handy (Issue #479); am Schreibtisch wirkungslos,
  // dort stehen sie fest in ihrer Rasterspalte.
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const narrow = useNarrowViewport();
  const [findings, setFindings] = useState<ValidateFinding[]>([]);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [borisOpen, setBorisOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [modbusMapOpen, setModbusMapOpen] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  // Blockpalette laden.
  useEffect(() => {
    let cancelled = false;
    getBlocks()
      .then((res) => !cancelled && setBlocks(res.blocks))
      .catch((err) => !cancelled && reportError(err, t('signal.paletteFailed')));
    return () => {
      cancelled = true;
    };
  }, [reportError, t]);

  // Superblock-Bibliothek laden (BORIS-Composites als zusammengeklappte Knoten; Defaults +
  // importierte .SBL, Issue #106). Best-effort: ist die Bibliothek nicht verfügbar, bleibt die
  // Palette ohne die Superblöcke-Gruppe. Als Callback gehalten, damit ein Import sie neu lädt.
  const reloadSuperblocks = useCallback(
    () =>
      getSuperblocks()
        .then((res) => setSuperblocks(res.superblocks))
        .catch(() => {
          /* keine Bibliothek — dann eben ohne Superblöcke */
        }),
    [],
  );

  useEffect(() => {
    void reloadSuperblocks();
  }, [reloadSuperblocks]);

  // Referenz → Ports (offene Terminals), damit ein geladener Superblock-Knoten seine Handles auch
  // ohne angeschlossene Kante zeigt (portsOf im graphToFlow kennt nur den Typ, nicht die Referenz).
  const superblockPorts = useMemo(() => {
    const m = new Map<string, { inputs: number[]; outputs: number[] }>();
    for (const s of superblocks) m.set(s.id, { inputs: s.inputs, outputs: s.outputs });
    return m;
  }, [superblocks]);

  // UNBEKANNTE Superblöcke (Issue #271): welche vom aktuellen Graphen referenzierten Superblöcke
  // fehlen in der Bibliothek (weder Default noch Import)? Rein clientseitig aus den Canvas-Knoten +
  // der geladenen Palette abgeleitet — das treibt den „unbekannter Superblock"-Zustand und den
  // Massenimport-Einstieg. (Der Backend-Endpoint …/superblocks/missing prüft dasselbe serverseitig.)
  const unknownSuperblockRefs = useMemo(() => {
    const known = new Set(superblocks.map((s) => s.id.toLowerCase()));
    const refs = new Map<string, string>(); // lower → Original-Schreibweise
    for (const n of nodes) {
      const data = n.data as GraphNodeData | undefined;
      if (data?.blockType !== 'superblock') continue;
      const ref = typeof data.params?.ref === 'string' ? (data.params.ref as string) : '';
      if (ref && !known.has(ref.toLowerCase())) refs.set(ref.toLowerCase(), ref);
    }
    return [...refs.values()].sort((a, b) => a.localeCompare(b));
  }, [nodes, superblocks]);

  // Modelle der Registry laden — sie speisen das Dropdown des model-inference-Knotens.
  useEffect(() => {
    let cancelled = false;
    listModels()
      .then((res) => !cancelled && setModels(res.models))
      .catch((err) => !cancelled && reportError(err, t('signal.modelsFailed')));
    return () => {
      cancelled = true;
    };
  }, [reportError, t]);

  // Vorhandenen Graphen des Projekts in den Canvas-Zustand übernehmen. Trägt ein Knoten keine
  // Position (geseedeter/gestempelter Graph), wird ein topologisches Links-nach-rechts-Layout
  // berechnet und angewandt — der geladene Graph ist damit lesbar statt ein Gitter. Zusätzlich
  // werden die Knoten in dezente Gruppen-Rahmen (nach Herkunft/Funktion) gebündelt; diese Rahmen
  // sind reiner Hintergrund (nicht editierbar, nicht persistiert).
  useEffect(() => {
    if (!graph) return;
    const portsOf = (type: string): { inputs: number[]; outputs: number[] } => {
      const special = specialNodePorts(type);
      if (special) return special;
      const b = blocks.find((x) => x.type === type);
      return { inputs: b?.inputs ?? [], outputs: b?.outputs ?? [] };
    };
    // Auto-Layout nur, wenn mindestens ein Knoten ohne eigene Position kommt.
    const needsLayout = graph.nodes.some((n) => !n.position);
    const layout = needsLayout ? computeLayout(graph.nodes, graph.edges) : undefined;
    const flow = graphToFlow(
      graph,
      portsOf,
      (i) => ({ x: 120 + (i % 4) * 200, y: 80 + Math.floor(i / 4) * 140 }),
      layout,
    );

    // Superblock-Knoten: Handles aus der Bibliotheks-Referenz setzen (offene Terminals), damit auch
    // unbeschaltete Ein-/Ausgänge sichtbar sind. portsOf kennt nur den Typ, nicht die Referenz.
    for (const n of flow.nodes) {
      if (n.data.blockType !== 'superblock') continue;
      const ref = typeof n.data.params?.ref === 'string' ? (n.data.params.ref as string) : '';
      const ports = superblockPorts.get(ref);
      if (ports) {
        n.data.inputs = ports.inputs;
        n.data.outputs = ports.outputs;
      } else {
        // Unbekannter Superblock (weder Default noch Import): auf der Canvas markieren (Issue #271).
        // Ports bleiben aus dem gespeicherten Graphen, damit die Verdrahtung sichtbar bleibt.
        n.data.unknownSuperblock = true;
      }
    }

    // Signalart je katalog-gebundenem Knoten nachschlagen (Issue #352). Der Graph trägt nur den
    // Tag; welche ART dahintersteht, weiß allein der Katalog — und ohne diesen Schritt sähe ein
    // exogener Vorhersagewert auf der Canvas aus wie ein Messwert der Anlage.
    const kindByTag = new Map((catalog?.variables ?? []).map((v) => [v.tag, kindOf(v)]));
    for (const n of flow.nodes) {
      const tag = typeof n.data.params?.tag === 'string' ? (n.data.params.tag as string) : '';
      const kind = tag ? kindByTag.get(tag) : undefined;
      if (kind) n.data.signalKind = kind;
    }

    // Gruppen-Rahmen aus den (effektiven) Knotenpositionen ableiten und als Hintergrund-Knoten
    // VOR die Signalknoten setzen, damit sie dahinter liegen.
    const positions = new Map(flow.nodes.map((n) => [n.id, n.position]));
    const boxes = groupBoxes(deriveGroups(graph.nodes), positions);
    const groupNodes: FlowGraphNode[] = boxes.map((b) => ({
      id: `group-${b.id}`,
      type: 'groupBox',
      position: { x: b.x, y: b.y },
      data: {
        blockType: 'group',
        label: b.label,
        params: {},
        inputs: [],
        outputs: [],
        functionLabel: b.function,
        width: b.width,
        height: b.height,
      } as unknown as GraphNodeData,
      selectable: false,
      draggable: false,
      focusable: false,
      zIndex: 0,
    }));

    // Der Aufbau ersetzt den Teil der Canvas, der aus dem Graphen stammt — und NUR den (#481).
    //
    // Dieser Effekt hängt nicht nur am Graphen, sondern auch an `blocks` und `superblockPorts`.
    // Die kommen aus zwei voneinander unabhängigen Anfragen, und die zweite trifft regelmäßig
    // erst ein, wenn der Anwender die Palette schon benutzt hat. Bis hierher hat der erneute
    // Aufbau dann alles überschrieben, was seitdem gesetzt worden war: der eben platzierte
    // Block war weg, ohne Meldung, und ob es passierte, entschied allein, welche Antwort
    // zuerst da war. Auf einem belasteten Bau-Agenten hat genau das die Vitest-Suite
    // unzuverlässig gemacht (#481) — der Test hat einen echten Defekt gefunden, keinen
    // Zeitmangel.
    //
    // Deshalb bleibt erhalten, was nicht aus dem Graphen stammt. Speichert der Anwender später,
    // steht der Knoten im Graphen und wird ab dann von dort aufgebaut; ein Doppel entsteht
    // nicht, weil die Herkunft über die Knoten-Kennung entschieden wird.
    //
    // NICHT abgedeckt: eine verschobene oder gelöschte Position eines Knotens AUS dem Graphen
    // setzt der erneute Aufbau weiterhin zurück. Das ist dieselbe Klasse und ein eigener
    // Vorgang — hier bewusst nicht mitgeändert, weil diese Funktion `kritikalitaet: hoch`
    // trägt und ein größerer Umbau eigene Nachweise braucht.
    const ausGraph = new Set(flow.nodes.map((n) => n.id));
    setNodes((vorher) => {
      const selbstGesetzt = vorher.filter(
        (n) => !ausGraph.has(n.id) && n.data.blockType !== 'group',
      );
      return [...groupNodes, ...flow.nodes, ...selbstGesetzt];
    });
    const kantenAusGraph = new Set(flow.edges.map((e) => e.id));
    setEdges((vorher) => [...flow.edges, ...vorher.filter((e) => !kantenAusGraph.has(e.id))]);
  }, [graph, blocks, superblockPorts, setNodes, setEdges]);

  // Palette-Suche über Blocktyp + Beschreibung + Kategorie. Leerer Text = alles.
  const grouped = useMemo(() => {
    const q = paletteSearch.trim().toLowerCase();
    const match = (b: BlockType): boolean =>
      !q ||
      [b.type, b.description ?? '', b.category].some((v) => v.toLowerCase().includes(q));
    const byCat = new Map<string, BlockType[]>();
    for (const b of blocks) {
      if (!match(b)) continue;
      const list = byCat.get(b.category) ?? [];
      list.push(b);
      byCat.set(b.category, list);
    }
    return [...byCat.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [blocks, paletteSearch]);

  // Sonder-/Katalog-Knoten sind ebenfalls durchsuchbar (nach Typ und übersetztem Label).
  const specialMatches = useMemo(() => {
    const q = paletteSearch.trim().toLowerCase();
    if (!q) return SPECIAL_TYPES as readonly string[];
    return SPECIAL_TYPES.filter(
      (type) => type.includes(q) || t(`signal.node.${type}`).toLowerCase().includes(q),
    );
  }, [paletteSearch, t]);

  // Ein Superblock ist „importiert" (per .SBL hochgeladen, Issue #106), wenn das Backend ihn so
  // markiert — bevorzugt origin, sonst das imported-Flag.
  const isImported = (s: SuperblockSummary): boolean =>
    s.origin === 'imported' || s.imported === true;

  // Default-Superblöcke (ausgeliefert) nach Familie gruppieren + über das Suchfeld filtern
  // (Name/Familie/Referenz). Importierte werden separat als eigene Gruppe geführt.
  const groupedSuperblocks = useMemo(() => {
    const q = paletteSearch.trim().toLowerCase();
    const match = (s: SuperblockSummary): boolean =>
      !q || [s.name, s.family, s.id].some((v) => v.toLowerCase().includes(q));
    const byFamily = new Map<string, SuperblockSummary[]>();
    for (const s of superblocks) {
      if (isImported(s) || !match(s)) continue;
      const list = byFamily.get(s.family) ?? [];
      list.push(s);
      byFamily.set(s.family, list);
    }
    return [...byFamily.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [superblocks, paletteSearch]);

  // Importierte Superblöcke (eigene Palette-Gruppe „Importiert") — über dasselbe Suchfeld gefiltert.
  const importedSuperblocks = useMemo(() => {
    const q = paletteSearch.trim().toLowerCase();
    return superblocks.filter(
      (s) =>
        isImported(s) &&
        (!q || [s.name, s.family, s.id].some((v) => v.toLowerCase().includes(q))),
    );
  }, [superblocks, paletteSearch]);

  const addBlockNode = useCallback(
    (
      type: string,
      inputs: number[],
      outputs: number[],
      label: string,
      params: Record<string, unknown> = {},
    ) => {
      const id = `n${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;
      setNodes((prev) => {
        const count = prev.length;
        const node: FlowGraphNode = {
          id,
          type: 'signal',
          position: { x: 160 + (count % 4) * 200, y: 100 + Math.floor(count / 4) * 140 },
          data: { blockType: type, label, params, inputs, outputs },
        };
        return [...prev, node];
      });
      setSelectedId(id);
    },
    [setNodes],
  );

  const addSuperblockNode = useCallback(
    (s: SuperblockSummary) => {
      addBlockNode('superblock', s.inputs, s.outputs, s.name, { ref: s.id });
    },
    [addBlockNode],
  );

  const addSpecial = useCallback(
    (type: (typeof SPECIAL_TYPES)[number]) => {
      const ports = specialNodePorts(type)!;
      addBlockNode(type, ports.inputs, ports.outputs, t(`signal.node.${type}`));
    },
    [addBlockNode, t],
  );

  const onConnect = useCallback(
    (conn: Connection) => setEdges((prev) => addEdge(conn, prev)),
    [setEdges],
  );

  // .SBL-Dateien aus dem lokalen Dateisystem in die instanzweite Superblock-Bibliothek importieren
  // (Issue #106). Nach dem Upload die Palette neu laden und das Ergebnis melden (importiert /
  // übersprungen mit Gründen). Das Recht „veröffentlichen" erzwingt das Backend (403).
  const onImportFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setImporting(true);
      try {
        const res = await importSuperblocks(files);
        await reloadSuperblocks();
        const skipped = res.skipped
          .map((s) => `${s.filename}: ${s.reason}`)
          .join('; ');
        if (res.imported.length > 0) {
          notify(
            'success',
            res.skipped.length > 0
              ? t('signal.importSuperblockPartial', {
                  imported: res.imported.length,
                  skipped: res.skipped.length,
                  reasons: skipped,
                })
              : t('signal.importSuperblockDone', { imported: res.imported.length }),
          );
        } else {
          notify('info', t('signal.importSuperblockNone', { reasons: skipped }));
        }
      } catch (err) {
        reportError(err, t('signal.importSuperblockFailed'));
      } finally {
        setImporting(false);
      }
    },
    [reloadSuperblocks, notify, reportError, t],
  );

  // Eine importierte .SBL wieder aus der Bibliothek entfernen und die Palette neu laden.
  const onRemoveImported = useCallback(
    async (ref: string) => {
      setImporting(true);
      try {
        await deleteSuperblock(ref);
        await reloadSuperblocks();
        notify('success', t('signal.importSuperblockRemoved', { ref }));
      } catch (err) {
        reportError(err, t('signal.importSuperblockRemoveFailed'));
      } finally {
        setImporting(false);
      }
    },
    [reloadSuperblocks, notify, reportError, t],
  );

  // Anzeigetitel eines Superblocks (aus der Bibliothek); fällt auf die Referenz zurück.
  const superblockTitle = useCallback(
    (ref: string) => superblocks.find((s) => s.id === ref)?.name ?? ref,
    [superblocks],
  );

  // Eine Superblock-Ebene öffnen: als erste Ebene des Drilldown-Stacks (vom Hauptgraphen aus).
  const openSuperblock = useCallback(
    (ref: string) => setDrilldown([{ ref, title: superblockTitle(ref) }]),
    [superblockTitle],
  );

  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null;

  const updateParam = useCallback(
    (nodeId: string, key: string, value: unknown) => {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, params: { ...n.data.params, [key]: value } } }
            : n,
        ),
      );
    },
    [setNodes],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setEdges((prev) => prev.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedId(null);
    },
    [setNodes, setEdges],
  );

  const currentGraph = useCallback(
    () =>
      // Die reinen Hintergrund-Gruppenrahmen (type 'groupBox') gehören nicht in das gespeicherte
      // graph.json — nur die echten Signalknoten. Ihre (Auto-Layout-)Positionen werden dabei
      // mitgeschrieben, damit ein erneutes Laden das Layout erhält.
      buildGraph(
        nodes.filter((n) => n.type !== 'groupBox'),
        edges,
        activeProjectId ? { project: activeProjectId } : undefined,
      ),
    [nodes, edges, activeProjectId],
  );

  const save = useCallback(async () => {
    if (!activeProjectId) return;
    setBusy(true);
    const doc = currentGraph();
    try {
      const res = await putGraph(activeProjectId, doc);
      setGraph(doc);
      setFindings([]);
      notify('success', t('signal.saved', { rev: res.rev }));
    } catch (err) {
      reportError(err, t('signal.saveFailed'));
    } finally {
      setBusy(false);
    }
  }, [activeProjectId, currentGraph, setGraph, notify, reportError, t]);

  const validate = useCallback(async () => {
    if (!activeProjectId) return;
    setBusy(true);
    try {
      const res = await validateGraph(activeProjectId, currentGraph());
      setFindings(res.findings);
      notify(res.valid ? 'success' : 'info', res.valid ? t('signal.valid') : t('signal.invalid'));
    } catch (err) {
      reportError(err, t('signal.validateFailed'));
    } finally {
      setBusy(false);
    }
  }, [activeProjectId, currentGraph, notify, reportError, t]);

  // Nach erfolgreichem BORIS-Import: das (ggf. neue) Zielprojekt aktiv setzen, Graph + Katalog
  // aus dem Import übernehmen und die Projektliste auffrischen (ein neues Projekt taucht so auf).
  const onImported = useCallback(
    (result: BorisImportResult) => {
      setCatalog(result.catalog);
      setGraph(result.graph);
      if (result.projectId !== activeProjectId) setActiveProjectId(result.projectId);
      listProjects()
        .then((res) => setProjects(res.projects))
        .catch(() => {
          /* Liste-Auffrischen ist nicht kritisch für den Import selbst */
        });
      const level = result.coverage.runnable ? 'success' : 'info';
      notify(
        level,
        result.coverage.runnable
          ? t('boris.notifyRunnable', { project: result.projectId })
          : t('boris.notifyPartial', { project: result.projectId }),
      );
    },
    [setCatalog, setGraph, setActiveProjectId, setProjects, activeProjectId, notify, t],
  );

  // Nach bestätigter Modbus-Register-Map-Zuordnung (#156): den Katalog frisch laden (die neuen
  // Variablen mit modbus-Block sind geschrieben) und den Dialog schließen. Rein lesend — kein
  // Schreibpfad zur Anlage.
  const onModbusMapConfirmed = useCallback(
    async (rev: string, count: number) => {
      if (activeProjectId) {
        try {
          setCatalog(await getCatalog(activeProjectId));
        } catch {
          /* Neuladen ist nicht kritisch für den Schreibvorgang selbst */
        }
      }
      notify('success', t('modbusMap.confirmed', { count, rev }));
      setModbusMapOpen(false);
    },
    [activeProjectId, setCatalog, notify, t],
  );

  // Der Import-Dialog wird an EINER stabilen Stelle (Fragment-Ende) gerendert, unabhängig vom
  // Branch (Projekt gewählt oder nicht). So bleibt sein interner Zustand (Abdeckungsbericht)
  // erhalten, auch wenn ein Import das aktive Projekt/den Branch wechselt — kein Remount.
  const modbusMapDialog =
    modbusMapOpen && activeProjectId ? (
      <ModbusMappingDialog
        projectId={activeProjectId}
        onClose={() => setModbusMapOpen(false)}
        onConfirmed={onModbusMapConfirmed}
      />
    ) : null;

  const borisDialog = borisOpen ? (
    <BorisImportDialog
      currentProjectId={activeProjectId}
      onClose={() => setBorisOpen(false)}
      onImported={onImported}
    />
  ) : null;

  // Massenimport-Dialog für unbekannte Superblöcke (Issue #271). Startzustand = die aktuell aus dem
  // Graphen abgeleiteten fehlenden Refs; der Dialog fährt den Mehrfach-Durchlauf und lädt nach jedem
  // Import die Palette neu, sodass unknownSuperblockRefs mitschrumpft.
  const bulkDialog = bulkOpen ? (
    <SuperblockBulkImportDialog
      initialMissing={unknownSuperblockRefs}
      onClose={() => setBulkOpen(false)}
      onImported={reloadSuperblocks}
    />
  ) : null;

  if (!activeProject) {
    return (
      <>
        <div className="p-8">
          <div className="flex flex-col items-center gap-4">
            <EmptyState title={t('common.noProject')} hint={t('common.noProjectHint')} />
            <span data-testid="signal-import-boris-empty">
              <Button onClick={() => setBorisOpen(true)}>{t('boris.button')}</Button>
            </span>
          </div>
        </div>
        {borisDialog}
      </>
    );
  }

  const catalogInputs = catalog?.variables.filter((v) => v.direction === 'input') ?? [];
  const catalogOutputs = catalog?.variables.filter((v) => v.direction === 'output') ?? [];

  return (
    <>
    <div className="flex h-full flex-col md:grid md:grid-cols-[16rem_1fr_20rem]">
      {/* Auf dem Handy holt diese Leiste die beiden weggeklappten Panele hervor. */}
      <CanvasDrawerBar
        testIdPrefix="signal"
        leftLabel={t('signal.palette')}
        rightLabel={t('signal.properties')}
        onOpenLeft={() => setPaletteOpen(true)}
        onOpenRight={() => setPropsOpen(true)}
      />

      {/* Palette */}
      <SidePanel
        side="left"
        testId="signal-palette-panel"
        title={t('signal.palette')}
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      >
        <span data-testid="signal-import-boris">
          <Button variant="primary" onClick={() => setBorisOpen(true)}>
            {t('boris.button')}
          </Button>
        </span>

        <span data-testid="signal-import-modbus-map">
          <Button onClick={() => setModbusMapOpen(true)}>{t('modbusMap.button')}</Button>
        </span>

        <input
          type="search"
          data-testid="signal-palette-search"
          value={paletteSearch}
          onChange={(e) => setPaletteSearch(e.target.value)}
          placeholder={t('signal.search')}
          className="rounded border border-bf-border bg-bf-navy-light px-2 py-1.5 text-sm text-bf-white placeholder:text-bf-muted focus:border-bf-gold focus:outline-none"
        />

        {specialMatches.length > 0 && (
          <>
            <PanelHeading>{t('signal.catalogNodes')}</PanelHeading>
            <div className="flex flex-col gap-1.5">
              {specialMatches.map((type) => (
                <button
                  key={type}
                  type="button"
                  data-testid={`signal-add-${type}`}
                  onClick={() => addSpecial(type as (typeof SPECIAL_TYPES)[number])}
                  className="rounded border border-bf-border px-2 py-1.5 text-left text-sm text-bf-white transition-colors hover:border-bf-gold"
                >
                  {t(`signal.node.${type}`)}
                </button>
              ))}
            </div>
          </>
        )}

        <PanelHeading>{t('signal.blocks')}</PanelHeading>
        {blocks.length === 0 && <p className="text-xs text-bf-muted">{t('common.loading')}</p>}
        {blocks.length > 0 && grouped.length === 0 && specialMatches.length === 0 && (
          <p className="text-xs text-bf-muted" data-testid="signal-palette-noresults">
            {t('signal.searchEmpty')}
          </p>
        )}
        {grouped.map(([cat, list]) => (
          <div key={cat} className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wider text-bf-muted">{cat}</span>
            {list.map((b) => (
              <button
                key={b.type}
                type="button"
                data-testid={`signal-add-block-${b.type}`}
                title={b.description}
                onClick={() =>
                  // `null` = variable Portzahl (DEQSYS/MULTIPLOT2/FCTN/JUNCTION/GENERATOR): das
                  // Backend kennt die Zahl noch nicht, das Frontend soll beim Platzieren aber
                  // trotzdem einen andockbaren Port (den ersten) anbieten, sonst hat der Knoten
                  // gar keinen Handle und man kann keine Kante befestigen (Issue #78). `??` greift
                  // NUR bei null/undefined — ein echtes leeres Array (Quelle/Senke wie DRAIN,
                  // `outputs: []`) bleibt bewusst handle-frei.
                  addBlockNode(b.type, b.inputs ?? [1], b.outputs ?? [1], b.type)
                }
                className="flex items-center justify-between rounded border border-bf-border px-2 py-1.5 text-left text-sm text-bf-white transition-colors hover:border-bf-gold"
              >
                <span>{b.type}</span>
                <span className="text-[10px] text-bf-muted">
                  {(b.inputs?.length ?? 0)}→{(b.outputs?.length ?? 0)}
                </span>
              </button>
            ))}
          </div>
        ))}

        {/* Superblöcke (BORIS-Bibliothek) — zusammengeklappte Composite-Bausteine, nach Familie
            gruppiert. Klick platziert einen zusammengeklappten Knoten; Doppelklick auf der Canvas
            klappt ihn (read-only) auf. Nicht lauffähige sind markiert. */}
        {groupedSuperblocks.length > 0 && (
          <>
            <PanelHeading>{t('signal.superblocks')}</PanelHeading>
            {groupedSuperblocks.map(([family, list]) => (
              <div key={family} className="flex flex-col gap-1" data-testid={`signal-sbfam-${family}`}>
                <span className="text-[10px] uppercase tracking-wider text-bf-muted">{family}</span>
                {list.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-testid={`signal-add-superblock-${s.id}`}
                    title={s.runnable ? s.name : `${s.name} — ${t('signal.superblockNotRunnable')}`}
                    onClick={() => addSuperblockNode(s)}
                    className="flex items-center justify-between gap-2 rounded border border-bf-border px-2 py-1.5 text-left text-sm text-bf-white transition-colors hover:border-bf-gold"
                  >
                    <span className="flex-1 truncate">
                      {s.name}
                      {!s.runnable && <span className="ml-1 text-[10px] text-amber-300">●</span>}
                    </span>
                    <span className="shrink-0 text-[10px] text-bf-muted">
                      {s.inputs.length}→{s.outputs.length}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </>
        )}

        {/* Importierte Superblöcke (.SBL, Issue #106) — instanzweit hochgeladene boris2py-Bausteine,
            als eigene Gruppe „Importiert" markiert. Klick platziert einen zusammengeklappten Knoten;
            Doppelklick auf der Canvas klappt ihn auf (wie die Defaults). Ein Entfernen-Icon je Import
            löscht die .SBL wieder. */}
        {importedSuperblocks.length > 0 && (
          <>
            <PanelHeading>{t('signal.importSuperblockGroup')}</PanelHeading>
            <div className="flex flex-col gap-1" data-testid="signal-imported-superblocks">
              {importedSuperblocks.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-1 rounded border border-bf-border px-2 py-1.5 text-sm text-bf-white transition-colors hover:border-bf-gold"
                >
                  <button
                    type="button"
                    data-testid={`signal-add-superblock-${s.id}`}
                    title={s.runnable ? s.name : `${s.name} — ${t('signal.superblockNotRunnable')}`}
                    onClick={() => addSuperblockNode(s)}
                    className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left"
                  >
                    <span className="flex-1 truncate">
                      {s.name}
                      {!s.runnable && <span className="ml-1 text-[10px] text-amber-300">●</span>}
                    </span>
                    <span className="shrink-0 text-[10px] text-bf-muted">
                      {s.inputs.length}→{s.outputs.length}
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid={`signal-remove-superblock-${s.id}`}
                    title={t('signal.importSuperblockRemove')}
                    aria-label={t('signal.importSuperblockRemove')}
                    disabled={importing}
                    onClick={() => void onRemoveImported(s.id)}
                    className="shrink-0 rounded px-1 text-xs text-bf-muted transition-colors hover:text-red-300 disabled:opacity-40"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Superblock aus .SBL importieren (Issue #106): verstecktes Datei-Feld + Button. Das
            Backend sichert das Recht „veröffentlichen" (403) — der Knopf bleibt für alle sichtbar. */}
        <input
          ref={importInputRef}
          type="file"
          accept=".sbl"
          multiple
          data-testid="signal-import-superblock-input"
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            // Das Feld zurücksetzen, damit dieselbe Datei erneut gewählt werden kann.
            e.target.value = '';
            void onImportFiles(files);
          }}
        />
        <span data-testid="signal-import-superblock">
          <Button
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? t('signal.importSuperblockBusy') : t('signal.importSuperblockButton')}
          </Button>
        </span>

        {/* Unbekannte Superblöcke (Issue #271): referenziert der Graph Superblöcke, die weder als
            Default noch als Import vorliegen, wird das hier sichtbar — mit dem Einstieg in den
            Massenimport (Mehrfach-Durchlauf bis alles aufgelöst ist). */}
        {unknownSuperblockRefs.length > 0 && (
          <div
            className="flex flex-col gap-1 rounded border border-amber-500/60 bg-amber-950/20 p-2"
            data-testid="signal-unknown-superblocks"
          >
            <PanelHeading>{t('signal.unknownSuperblocks')}</PanelHeading>
            <p className="text-xs text-amber-200">
              {t('signal.unknownSuperblocksHint', { count: unknownSuperblockRefs.length })}
            </p>
            <ul className="max-h-28 overflow-y-auto text-xs text-amber-200/90">
              {unknownSuperblockRefs.map((ref) => (
                <li key={ref} className="truncate" title={ref}>
                  {ref}
                </li>
              ))}
            </ul>
            <span data-testid="signal-open-bulk-import">
              <Button variant="primary" onClick={() => setBulkOpen(true)}>
                {t('signal.bulkImportOpen')}
              </Button>
            </span>
          </div>
        )}
      </SidePanel>

      {/* Canvas */}
      <div className="relative min-h-0 flex-1 md:flex-none">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          onNodeClick={(_, n) => setSelectedId(n.id)}
          onNodeDoubleClick={(_, n) => {
            const ref = (n.data as GraphNodeData)?.params?.ref;
            if ((n.data as GraphNodeData)?.blockType === 'superblock' && typeof ref === 'string') {
              openSuperblock(ref);
            }
          }}
          onPaneClick={() => setSelectedId(null)}
          fitView
          // Große Graphen (600+ Knoten, jeder Sensor mit voller Konditionierungskette) brauchen
          // einen niedrigen minZoom, damit fitView die ganze Verdrahtung ins Bild rahmt; die Kanten
          // bleiben dank non-scaling-stroke (index.css) auch weit herausgezoomt sichtbar.
          minZoom={0.05}
          // Einrahmen beim Öffnen: am Schreibtisch die Übersicht über den ganzen Graphen. Auf dem
          // Handy würde derselbe Ausschnitt die Beschriftungen zu einem Schmierstreifen stauchen,
          // deshalb dort ein Zoom-Boden — der Graph ist dann lesbar und wird geschoben. Die
          // Übersicht bleibt über den „Einpassen"-Knopf der Steuerleiste einen Tipp entfernt.
          fitViewOptions={{ padding: 0.15, minZoom: narrow ? 0.5 : undefined }}
          defaultEdgeOptions={{ style: { strokeWidth: 1.5 } }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#1A3460" gap={20} />
          <Controls className="!bg-bf-navy-light !text-bf-white" />
        </ReactFlow>
        {nodes.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8">
            <EmptyState title={t('signal.canvasEmpty')} hint={t('signal.canvasHint')} />
          </div>
        )}
      </div>

      {/* Eigenschaften */}
      <SidePanel
        side="right"
        testId="signal-properties-panel"
        title={t('signal.properties')}
        open={propsOpen}
        onClose={() => setPropsOpen(false)}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid="signal-save">
            <Button variant="primary" onClick={save} disabled={busy || nodes.length === 0}>
              {t('signal.save')}
            </Button>
          </span>
          <span data-testid="signal-validate">
            <Button onClick={validate} disabled={busy || nodes.length === 0}>
              {t('signal.validate')}
            </Button>
          </span>
        </div>

        {findings.length > 0 && (
          <div
            data-testid="signal-findings"
            className="flex flex-col gap-1 rounded border border-red-500/50 bg-red-950/30 p-2"
          >
            <PanelHeading>{t('signal.findings')}</PanelHeading>
            {findings.map((f, i) => (
              <div key={i} className="text-xs">
                <span
                  className={
                    f.severity === 'error' ? 'font-semibold text-red-300' : 'text-amber-300'
                  }
                >
                  {f.code}
                </span>
                <span className="text-bf-muted"> — {f.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Die Panel-Überschrift trägt SidePanel; hier folgt direkt der Knoten-Teil. */}
        {!selectedNode && (
          <EmptyState title={t('signal.selectNode')} hint={t('signal.selectNodeHint')} />
        )}

        {selectedNode && (
          <NodeParams
            node={selectedNode}
            catalogInputs={catalogInputs}
            catalogOutputs={catalogOutputs}
            models={models}
            onParam={(key, value) => updateParam(selectedNode.id, key, value)}
            onRemove={() => removeNode(selectedNode.id)}
          />
        )}
      </SidePanel>
    </div>
    {borisDialog}
    {bulkDialog}
    {modbusMapDialog}
    {drilldown.length > 0 && (
      <SuperblockEditor
        stack={drilldown}
        titleOf={superblockTitle}
        onPush={(ref) =>
          setDrilldown((prev) =>
            // Idempotent: dieselbe Referenz nicht zweimal in Folge auf den Stack schieben (ein
            // Doppelklick darf keine doppelte Ebene erzeugen).
            prev[prev.length - 1]?.ref === ref
              ? prev
              : [...prev, { ref, title: superblockTitle(ref) }],
          )
        }
        onBack={() => setDrilldown((prev) => prev.slice(0, -1))}
        onClose={() => setDrilldown([])}
      />
    )}
    </>
  );
}

// Eine Ebene des Drilldown-Stacks: die Superblock-Referenz und ihr Anzeigetitel für die Breadcrumb.
interface SuperblockLevel {
  ref: string;
  title: string;
}

// Aufklapp-Editor eines Superblocks mit verschachteltem Drilldown. Zeigt immer die oberste Ebene
// des Navigations-Stacks: lädt deren inneren Signalgraphen (getSuperblockGraph) und zeichnet ihn in
// einem eigenen React-Flow. Ein Doppelklick auf einen darin enthaltenen Superblock-Knoten öffnet
// eine weitere Ebene (onPush); die Breadcrumb (Hauptgraph › SB-A › SB-B) zeigt den Pfad, „Zurück"
// poppt genau eine Ebene. Der innere Graph ist editierbar (Ports verdrahten, Parameter ansehen/
// ändern) statt read-only — die Änderungen bleiben lokal im Aufklapp-Editor (die Bibliotheks-.SBL
// wird nicht überschrieben).
function SuperblockEditor({
  stack,
  titleOf,
  onPush,
  onBack,
  onClose,
}: {
  stack: SuperblockLevel[];
  titleOf: (ref: string) => string;
  onPush: (ref: string) => void;
  onBack: () => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const current = stack[stack.length - 1];

  // Escape schließt den Aufklapp-Editor (Tastaturbedienung); ein Klick daneben ebenfalls.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 md:p-6"
      data-testid="superblock-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={current.title}
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full flex-col rounded-lg border border-bf-border bg-bf-navy md:h-[80vh] md:w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-bf-border px-3 py-2 md:px-4 md:py-3">
          {/* Breadcrumb: Hauptgraph › SB-A › SB-B. Jede vorherige Ebene bleibt sichtbar; nur die
              oberste Ebene ist die aktuell gezeigte. Ein Klick auf „Hauptgraph" schließt. */}
          <nav
            data-testid="superblock-breadcrumb"
            className="flex min-w-0 flex-wrap items-center gap-1 text-sm"
            aria-label={t('signal.superblockInner')}
          >
            <button
              type="button"
              data-testid="superblock-crumb-root"
              onClick={onClose}
              className="shrink-0 text-bf-muted transition-colors hover:text-bf-gold"
            >
              {t('signal.superblockRoot')}
            </button>
            {stack.map((level, i) => {
              const isLast = i === stack.length - 1;
              return (
                <span key={`${level.ref}-${i}`} className="flex min-w-0 items-center gap-1">
                  <span className="shrink-0 text-bf-muted">›</span>
                  <span
                    data-testid={`superblock-crumb-${i}`}
                    className={
                      isLast
                        ? 'truncate font-semibold text-bf-white'
                        : 'truncate text-bf-muted'
                    }
                  >
                    {level.title}
                  </span>
                </span>
              );
            })}
          </nav>
          <div className="flex shrink-0 items-center gap-2">
            {stack.length > 1 && (
              <Button testId="superblock-back" onClick={onBack}>
                {t('signal.superblockBack')}
              </Button>
            )}
            <Button onClick={onClose}>{t('common.close')}</Button>
          </div>
        </div>
        {/* Explizite Höhe (statt flex-1): der Dialog-Canvas soll beim Mount eine stabile Größe
            haben, damit React-Flows fitView den Innen-Graphen korrekt einrahmt. Der Level wird über
            key neu gemountet, damit jede Ebene ihren eigenen, frisch geladenen Graphen zeigt. */}
        <div className="relative h-[calc(90vh-6rem)] md:h-[calc(80vh-3.25rem)]">
          <SuperblockLevelCanvas
            key={`${current.ref}-${stack.length}`}
            superblockRef={current.ref}
            titleOf={titleOf}
            onDrill={onPush}
          />
        </div>
      </div>
    </div>
  );
}

// Der Canvas EINER Drilldown-Ebene: lädt den inneren Signalgraphen des Superblocks und macht ihn
// editierbar (Knoten verschieben, Ports verdrahten). Ein Doppelklick auf einen verschachtelten
// Superblock-Knoten meldet die Referenz nach oben (onDrill) — so navigiert man eine Ebene tiefer.
function SuperblockLevelCanvas({
  superblockRef,
  titleOf,
  onDrill,
}: {
  superblockRef: string;
  titleOf: (ref: string) => string;
  onDrill: (ref: string) => void;
}): JSX.Element {
  const { t } = useI18n();
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowGraphNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<FlowGraphEdge>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const toFlow = (g: SignalGraph): void => {
      if (cancelled) return;
      const portsOf = (type: string): { inputs: number[]; outputs: number[] } => {
        const special = specialNodePorts(type);
        return special ?? { inputs: [], outputs: [] };
      };
      const layout = computeLayout(g.nodes, g.edges);
      const flow = graphToFlow(
        g,
        portsOf,
        (i) => ({ x: 120 + (i % 4) * 200, y: 80 + Math.floor(i / 4) * 140 }),
        layout,
      );
      setNodes(flow.nodes);
      setEdges(flow.edges);
      setLoaded(true);
    };

    getSuperblockGraph(superblockRef)
      .then((res) => toFlow(res.graph))
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [superblockRef, setNodes, setEdges]);

  const onConnect = useCallback(
    (conn: Connection) => setEdges((prev) => addEdge(conn, prev)),
    [setEdges],
  );

  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null;

  if (error) {
    return (
      <div className="p-8">
        <EmptyState title={t('signal.superblockInnerFailed')} hint={superblockRef} />
      </div>
    );
  }
  if (!loaded) return <div className="p-8 text-xs text-bf-muted">{t('common.loading')}</div>;

  return (
    <div className="flex h-full flex-col md:grid md:grid-cols-[1fr_16rem]">
      {/* React-Flow braucht einen Wrapper mit eigener Höhe — als direktes Flex-Kind bekäme es
          auf dem Handy keine (Issue #479). Im Raster ist der Wrapper einfach die linke Spalte. */}
      <div className="relative min-h-0 flex-1 md:flex-none">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onNodeClick={(_, n) => setSelectedId(n.id)}
        onNodeDoubleClick={(_, n) => {
          // Verschachteltes Öffnen: ein Superblock-Knoten IM inneren Graphen navigiert eine Ebene
          // tiefer (statt read-only zu bleiben). Die Referenz kommt aus params.ref.
          const ref = (n.data as GraphNodeData)?.params?.ref;
          if ((n.data as GraphNodeData)?.blockType === 'superblock' && typeof ref === 'string') {
            onDrill(ref);
          }
        }}
        onPaneClick={() => setSelectedId(null)}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        // Große Superblöcke haben viele Knoten — der Default-minZoom (0.5) klemmt fitView, so dass
        // der Graph nicht ganz ins Bild passt. Niedriger minZoom lässt ihn vollständig rahmen.
        minZoom={0.05}
        // Zusätzlich ein fitView, sobald React-Flow die Custom-Knoten vermessen hat (nach dem
        // ersten Paint), damit der Ausschnitt sicher sitzt.
        onInit={(instance) => {
          requestAnimationFrame(() => instance.fitView({ padding: 0.2 }));
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#1A3460" gap={20} />
        <Controls className="!bg-bf-navy-light !text-bf-white" />
      </ReactFlow>
      </div>

      {/* Eigenschaften der gewählten Innen-Knoten: Parameter ansehen/bearbeiten. Ist ein
          verschachtelter Superblock gewählt, weist ein Hinweis auf den Doppelklick-Drilldown hin. */}
      <aside className="flex max-h-[45%] shrink-0 flex-col gap-3 overflow-y-auto border-t border-bf-border p-3 md:max-h-none md:border-l md:border-t-0">
        <PanelHeading>{t('signal.properties')}</PanelHeading>
        {!selectedNode && (
          <EmptyState title={t('signal.selectNode')} hint={t('signal.selectNodeHint')} />
        )}
        {selectedNode && (
          <div className="flex flex-col gap-3">
            <div className="rounded border border-bf-border p-2">
              <div className="text-xs font-semibold text-bf-white">
                {(selectedNode.data as GraphNodeData).label}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-bf-muted">
                {(selectedNode.data as GraphNodeData).blockType}
              </div>
            </div>
            {(selectedNode.data as GraphNodeData).blockType === 'superblock' && (
              <p className="text-xs text-bf-muted" data-testid="superblock-drill-hint">
                {t('signal.superblockDrillHint', {
                  name: titleOf(
                    String((selectedNode.data as GraphNodeData).params?.ref ?? ''),
                  ),
                })}
              </p>
            )}
            <SuperblockInnerParams
              params={(selectedNode.data as GraphNodeData).params ?? {}}
              onParam={(key, value) =>
                setNodes((prev) =>
                  prev.map((n) =>
                    n.id === selectedNode.id
                      ? { ...n, data: { ...n.data, params: { ...n.data.params, [key]: value } } }
                      : n,
                  ),
                )
              }
            />
          </div>
        )}
      </aside>
    </div>
  );
}

// Schlanker Parameter-Editor für die Innen-Knoten einer Drilldown-Ebene: zeigt die vorhandenen
// Parameter als Schlüssel/Wert. Boris-Metafelder (borisIndex/borisType/borisName/placeholder) sind
// Herkunfts-Marker und bleiben schreibgeschützt; fachliche Parameter sind editierbar.
const _READONLY_PARAM_KEYS = new Set(['borisIndex', 'borisType', 'borisName', 'placeholder']);
function SuperblockInnerParams({
  params,
  onParam,
}: {
  params: Record<string, unknown>;
  onParam: (key: string, value: unknown) => void;
}): JSX.Element {
  const { t } = useI18n();
  const entries = Object.entries(params);
  return (
    <div className="flex flex-col gap-2">
      <PanelHeading>{t('signal.params')}</PanelHeading>
      {entries.length === 0 && <p className="text-xs text-bf-muted">{t('signal.noParams')}</p>}
      {entries.map(([key, value]) =>
        _READONLY_PARAM_KEYS.has(key) ? (
          <div key={key} className="text-[11px] text-bf-muted" data-testid={`superblock-meta-${key}`}>
            <span className="uppercase tracking-wider">{key}</span>: {String(value ?? '')}
          </div>
        ) : (
          <span key={key} data-testid={`superblock-param-${key}`}>
            <LabeledInput
              label={key}
              value={String(value ?? '')}
              onChange={(v) => onParam(key, coerce(v))}
            />
          </span>
        ),
      )}
    </div>
  );
}

// Tag-Auswahl, nach Signalart gruppiert (Issue #352). Ohne die Gruppierung gingen exogene Größen
// und Zustandssignale zwischen hunderten Anlagen-Tags unter — und genau die will man beim
// Verdrahten auseinanderhalten: eine Vorhersage ist keine Rückkopplung, ein Verschleißsignal
// gehört nicht an einen Modellknoten.
function TagOptions({ variables }: { variables: CatalogVariable[] }): JSX.Element {
  const { t } = useI18n();
  const gruppen = groupByKind(variables);
  // Nur eine Gruppe? Dann ist die Überschrift Ballast — die Liste bleibt flach.
  if (gruppen.length <= 1) {
    return (
      <>
        {variables.map((v) => (
          <option key={v.tag} value={v.tag}>
            {anzeigeMitBeleg(v)}
          </option>
        ))}
      </>
    );
  }
  return (
    <>
      {gruppen.map((g) => (
        <optgroup key={g.kind} label={t(`signal.kind.${g.kind}`)}>
          {g.variables.map((v) => (
            <option key={v.tag} value={v.tag}>
              {anzeigeMitBeleg(v)}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

// Eigenschaften-Editor je Knotenart: Katalog-Tag-Dropdown für catalog-*, Modell-/Versions-Auswahl
// für model-inference, freie Schlüssel/Wert-Parameter für boris2py-Blöcke (z. B. CONST-Wert,
// FCT1-Formel).
function NodeParams({
  node,
  catalogInputs,
  catalogOutputs,
  models,
  onParam,
  onRemove,
}: {
  node: FlowGraphNode;
  catalogInputs: CatalogVariable[];
  catalogOutputs: CatalogVariable[];
  models: ModelSummary[];
  onParam: (key: string, value: unknown) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const data = node.data as GraphNodeData;
  const params = data.params ?? {};

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded border border-bf-border p-2">
        <div className="text-xs font-semibold text-bf-white">{data.label}</div>
        <div className="text-[10px] uppercase tracking-wider text-bf-muted">{data.blockType}</div>
      </div>

      {data.blockType === 'catalog-input' && (
        <span data-testid="signal-tag-input">
          <LabeledSelect
            label={t('signal.tag')}
            value={typeof params.tag === 'string' ? params.tag : ''}
            onChange={(v) => onParam('tag', v)}
          >
            <option value="">{t('signal.chooseTag')}</option>
            <TagOptions variables={catalogInputs} />
          </LabeledSelect>
        </span>
      )}

      {/* signal-filter: nur die Tag-Wahl. Art und Zeitkonstante der Glättung stehen im
          Signalkatalog (Issue #337) — sie hier editierbar zu machen, würde genau die zweite
          Wahrheitsquelle schaffen, die der Knoten vermeiden soll. */}
      {data.blockType === 'signal-filter' && (
        <>
          <span data-testid="signal-tag-filter">
            <LabeledSelect
              label={t('signal.tag')}
              value={typeof params.tag === 'string' ? params.tag : ''}
              onChange={(v) => onParam('tag', v)}
            >
              <option value="">{t('signal.chooseTag')}</option>
              <TagOptions variables={catalogInputs} />
            </LabeledSelect>
          </span>
          <p className="text-[10px] text-bf-muted" data-testid="signal-filter-hint">
            {t('signal.filterFromCatalog')}
          </p>
        </>
      )}

      {data.blockType === 'catalog-output' && (
        <span data-testid="signal-tag-output">
          <LabeledSelect
            label={t('signal.tag')}
            value={typeof params.tag === 'string' ? params.tag : ''}
            onChange={(v) => onParam('tag', v)}
          >
            <option value="">{t('signal.chooseTag')}</option>
            <TagOptions variables={catalogOutputs} />
          </LabeledSelect>
        </span>
      )}

      {data.blockType === 'model-inference' && (
        <ModelInferenceParams params={params} models={models} onParam={onParam} />
      )}

      {!SPECIAL_TYPES.includes(data.blockType as (typeof SPECIAL_TYPES)[number]) && (
        <FreeParams params={params} onParam={onParam} />
      )}

      <Button variant="danger" onClick={onRemove}>
        {t('signal.removeNode')}
      </Button>
    </div>
  );
}

// Frei editierbare params (Schlüssel/Wert) für boris2py-Blöcke. Werte werden als Zahl gespeichert,
// wenn sie sich als Zahl parsen lassen (CONST-Wert), sonst als String (FCT1-Formel).
function FreeParams({
  params,
  onParam,
}: {
  params: Record<string, unknown>;
  onParam: (key: string, value: unknown) => void;
}): JSX.Element {
  const { t } = useI18n();
  const [newKey, setNewKey] = useState('');

  const entries = Object.entries(params);
  return (
    <div className="flex flex-col gap-2">
      <PanelHeading>{t('signal.params')}</PanelHeading>
      {entries.length === 0 && <p className="text-xs text-bf-muted">{t('signal.noParams')}</p>}
      {entries.map(([key, value]) => (
        <span key={key} data-testid={`signal-param-${key}`}>
          <LabeledInput
            label={key}
            value={String(value ?? '')}
            onChange={(v) => onParam(key, coerce(v))}
          />
        </span>
      ))}
      <div className="flex items-end gap-2">
        <div className="flex-1" data-testid="signal-new-param-key">
          <LabeledInput
            label={t('signal.newParam')}
            value={newKey}
            onChange={setNewKey}
            placeholder="T"
          />
        </div>
        <span data-testid="signal-add-param">
          <Button
            onClick={() => {
              const k = newKey.trim();
              if (k) {
                onParam(k, '');
                setNewKey('');
              }
            }}
          >
            +
          </Button>
        </span>
      </div>
    </div>
  );
}

// Modell-Inferenz-Parameter: Modell und Version aus der Registry wählen. Die Auswahl wird als
// params.artifact = {name, version} in den Graphen geschrieben, so wie sie der Rechenkern und die
// Registry-Bindung erwarten. Das Version-Dropdown zeigt je Version den Governance-Status; nur
// freigegebene Versionen sind später live lauffähig (die Validierung setzt das durch).
function ModelInferenceParams({
  params,
  models,
  onParam,
}: {
  params: Record<string, unknown>;
  models: ModelSummary[];
  onParam: (key: string, value: unknown) => void;
}): JSX.Element {
  const { t } = useI18n();
  const name = artifactField(params, 'name');
  const version = artifactField(params, 'version');
  const selectedModel = models.find((m) => m.model_id === name) ?? null;

  // Modellwechsel: Version auf die aktive (current) bzw. die erste vorhandene vorbelegen, damit
  // die Referenz gleich vollständig ist.
  const chooseModel = (nextName: string): void => {
    const model = models.find((m) => m.model_id === nextName) ?? null;
    const nextVersion =
      model?.current ?? model?.versions[0]?.version ?? '';
    onParam('artifact', { name: nextName, version: nextVersion });
  };

  const chooseVersion = (nextVersion: string): void => {
    onParam('artifact', { ...artifactObj(params), name, version: nextVersion });
  };

  return (
    <div className="flex flex-col gap-2">
      <span data-testid="signal-model-name">
        <LabeledSelect label={t('signal.model')} value={name} onChange={chooseModel}>
          <option value="">{t('signal.chooseModel')}</option>
          {models.map((m) => (
            <option key={m.model_id} value={m.model_id}>
              {m.model_id}
            </option>
          ))}
        </LabeledSelect>
      </span>

      <span data-testid="signal-model-version">
        <LabeledSelect
          label={t('signal.modelVersion')}
          value={version}
          onChange={chooseVersion}
        >
          <option value="">{t('signal.chooseVersion')}</option>
          {(selectedModel?.versions ?? []).map((v) => (
            <option key={v.version} value={v.version}>
              {v.version} · {v.status}
            </option>
          ))}
        </LabeledSelect>
      </span>

      {models.length === 0 && (
        <p className="text-xs text-bf-muted">{t('signal.noModels')}</p>
      )}
    </div>
  );
}

// Hilfen für die Artefakt-Referenz (params.artifact = {name, version}).
function artifactObj(params: Record<string, unknown>): Record<string, unknown> {
  const a = params.artifact;
  return a && typeof a === 'object' ? (a as Record<string, unknown>) : {};
}
function artifactField(params: Record<string, unknown>, key: string): string {
  const a = artifactObj(params);
  return typeof a[key] === 'string' ? (a[key] as string) : '';
}

// Zahl, wenn parsebar, sonst Roh-String.
function coerce(v: string): number | string {
  if (v.trim() === '') return '';
  const n = Number(v);
  return Number.isFinite(n) && String(n) === v.trim() ? n : v;
}
